import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  KeyOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Form,
  Layout,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  TableColumnsType,
  TableProps,
  Tooltip,
} from "antd";
import { get, isEmpty } from "lodash";
import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DetailDepartmentalUserAccountDrawer from "../../components/drawer/departmental-account";
import ChangePassDepartmentDrawer from "../../components/drawer/departmental-account/change-password";
import PageHeader from "../../components/page-header/PageHeader";
import TableData from "../../components/table-data/TableData";
import { Permission } from "../../layout/sidebar/permission.enum";
import { HelpService } from "../../service/helper.service";
import {
  getOffices,
  getRoles,
  getUnits,
} from "../../service/list-account-users/listAccountUsersApi";
import {
  changeStatusAccountMinistry,
  createAccountMinistry,
  deleteAccountMinistry,
  getAllMinistryAccount,
} from "../../service/list-acoount-ministry/listAccountMinistry";
import { globalStore } from "../../stores/stores";
import { DataType } from "../../types/listUsers";
import { formatOriginTime } from "../../utils/converDate";
import { getCurrentPageFromLink } from "../../utils/getPageFromLink";
import FilterTable from "../list-ministries/components/filter-table/FilterTable";
import FormFilter from "./components/filter-group/FormFilter";
import FormCreateMinistry from "./components/form-create-ministry/FormCreateMinistry";
import { validateFormCreate } from "./constants/data";
import "./list-ministries.scss";

type ListMinistriesProps = {
  title?: string;
};

const ListMinistries = ({ title }: ListMinistriesProps) => {
  const { t } = useTranslation();
  const helpService = new HelpService();
  const [params, setParams] = useSearchParams();
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isOpenLecturerAccountAdmin, setIsOpenLecturerAccountAdmin] =
    useState<boolean>(false);
  const [isOpenChangePass, setIsOpenChangePass] = useState<boolean>(false);
  const [statusOrder, setStatusOrder] = useState<string>("desc");
  const [fieldSortOrder, setFieldSortOrder] = useState<string>("dateJoined");
  const [accountUsers, setAccountUsers] = useState<DataType[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { personalInfo, reload } = globalStore();
  const permissions = personalInfo?.permissions;

  const [dataOptions, setDataOptions] = useState({
    dataUnits: [],
    dataRoles: [],
    dataOffices: [],
  });
  const [filterData, setFilterData] = useState<any>({
    universityId: null,
    status: null,
    approvedDate: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchValue, setSearchValue] = useState<string>("");
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [countItem, setCountItem] = useState<number>(0);
  const [detailData, setDetailData] = useState<any>();

  const options = [
    ...(permissions?.includes(Permission.ChangeMUAccountUpdate)
      ? [
          {
            key: "0",
            label: t("Sửa tài khoản", "Sửa tài khoản"),
            icon: <EditOutlined />,
          },
          {
            key: "1",
            label: t("Đổi mật khẩu", "Đổi mật khẩu"),
            icon: <KeyOutlined />,
          },
        ]
      : []),
    ...(permissions?.includes(Permission.DeleteMUAccountUpdate)
      ? [
          {
            key: "2",
            label: t("Xóa tài khoản", "Xóa tài khoản"),
            icon: <DeleteOutlined />,
            className: "dropdown-delete",
            danger: true,
          },
        ]
      : []),
  ];

  const getListData = async () => {
    setLoading(true);
    const data = {
      page: pageNumber,
      size: pageSize,
      sort: statusOrder ? [`${fieldSortOrder},${statusOrder}`] : [],
      keyword: searchValue.trim() || undefined,
      enterpriseId: null,
      organizationId: filterData.universityId ?? null,
      createdFromDate:
        !isEmpty(filterData.approvedDate) &&
        !isEmpty(filterData.approvedDate?.[0])
          ? `${filterData.approvedDate?.[0]}T00:00:00.000Z`
          : null,
      createdToDate:
        !isEmpty(filterData.approvedDate) &&
        !isEmpty(filterData.approvedDate?.[1])
          ? `${filterData.approvedDate?.[1]}T00:00:00.000Z`
          : null,
      status: !filterData.status
        ? null
        : filterData.status === 1
        ? false
        : true,
    };

    await getAllMinistryAccount(data)
      .then((res) => {
        const { data } = res.data;
        setAccountUsers(data.data);
        setTotalRecords(data.total);
        setCountItem(data?.count);
        setTimeout(() => {
          setLoading(false);
        }, 500);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          navigate("/error/403");
        } else {
          console.error(err);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    getListData();
  }, [pageNumber, statusOrder, pageSize, reload]);

  const dataColumnsMinistries: TableColumnsType<DataType> = [
    {
      title: "STT",
      dataIndex: "numericalOrder",
      key: "numericalOrder",
      width: 10,
      render: (_: any, record: any, index: number) => {
        return <div>{record?.numericalOrder}</div>;
      },
    },
    {
      title: t("Tên đăng nhập", "Tên đăng nhập"),
      dataIndex: "username",
      key: "username",
      sorter: true,
      width: 30,
    },
    {
      title: t("Họ và tên người dùng", "Họ và tên người dùng"),
      dataIndex: "name",
      key: "name",
      width: 30,
    },
    {
      title: t("Đơn vị công tác", "Đơn vị công tác"),
      dataIndex: "unitName",
      key: "unitName",
      width: 20,
    },
    {
      title: t("Vai trò", "Vai trò"),
      dataIndex: "vaiTros",
      key: "vaiTros",
      width: 30,
      render: (_: any, record: any) => {
        const roles = record?.roles || [];

        const content = roles?.map((item: any, index: number) => (
          <p
            key={index}
            className="border rounded-md w-fit p-1 mt-1 bg-gray-100 break-all"
          >
            {item?.name}
          </p>
        ));

        return (
          <Tooltip
            title={
              <div style={{ maxHeight: "30vh", overflow: "auto" }}>
                {roles.map((item: any, idx: number) => (
                  <div key={idx}>{item?.name}</div>
                ))}
              </div>
            }
            style={{ padding: 5 }}
            mouseEnterDelay={0.5}
            placement="topLeft"
          >
            <div
              style={{
                maxHeight: "10vh",
                overflow: "hidden",
              }}
            >
              {content}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t("Ngày tạo", "Ngày tạo"),
      dataIndex: "createdDate",
      key: "createdDate",
      width: 20,
      render: (_: any, record: any) => {
        return (
          <>
            {record?.dateJoined ? formatOriginTime(record?.dateJoined) : null}
          </>
        );
      },
    },
    {
      title: t("Trạng thái", "Trạng thái"),
      dataIndex: "status",
      key: "status",
      // shouldCellUpdate(record, prevRecord) {
      //   return true
      // },
      render: (_: any, record: any) => {
        const handleChange = (
          checked: boolean,
          event: React.MouseEvent<HTMLButtonElement>
        ) => {
          record.isActive = checked;
        };

        const confirm = (id: number) => {
          const data = {
            usedId: record?.id,
            isActive: record?.isActive,
          };
          changeStatusAccountMinistry(data).then((res) => {
            if (res.status === 200) {
              // record.isActive = !record?.isActive;
              helpService.successMessage(
                `${record?.isActive ? t("Mở khóa", "Mở khóa") : t("Khóa", "Khóa")} ${t("tài khoản thành công", "tài khoản thành công")}`
              );
              getListData();
            }
          });
        };

        const cancel = () => {};

        if (permissions?.includes(Permission.LockUnlockMUAccountUpdate)) {
          return (
            <Popconfirm
              description={`${t("Bạn có chắc chắn muốn", "Bạn có chắc chắn muốn")} ${
                record.isActive ? t("khóa", "khóa") : t("mở khóa", "mở khóa")
              } ${t("tài khoản?", "tài khoản?")}`}
              overlayClassName="change-status-popconfirm"
              onConfirm={() => confirm(get(record, "id"))}
              onCancel={cancel}
              okText={record.isActive ? t("Khóa", "Khóa") : t("Mở", "Mở")}
              cancelText={t("Huỷ bỏ", "Huỷ bỏ")}
              title=""
            >
              <Switch
                defaultChecked={record.isActive}
                checked={record.isActive}
                onChange={handleChange}
              />
            </Popconfirm>
          );
        } else {
          return (
            <Switch
              defaultChecked={record.isActive}
              checked={record.isActive}
              onChange={() => message.error(t("Không có quyền chỉnh sửa!", "Không có quyền chỉnh sửa!"))}
            />
          );
        }
      },
      width: 20,
    },
    {
      title: "",
      dataIndex: "action",
      key: "operation",
      render: (_, record) => {
        return (
          <Space className="flex justify-center">
            <a
              className="action-table"
              onClick={() => {
                setIsOpenLecturerAccountAdmin(true);
                setDetailData(record);
              }}
            >
              <EyeOutlined />
            </a>
            <Dropdown
              menu={{
                items: options,
                onClick: ({ key }) => {
                  if (key === "0") {
                    setIsOpened(true);
                    setDetailData(record);
                    setIsEdit(true);
                  }
                  if (key === "1") {
                    setIsOpenChangePass(true);
                    setDetailData(record);
                    setIsEdit(false);
                  }
                  if (key === "2") {
                    Modal.confirm({
                      title: t("Xóa tài khoản", "Xóa tài khoản"),
                      content: t("Dữ liệu đã xoá sẽ không thể khôi phục. Bạn có chắc chắn muốn xoá?", "Dữ liệu đã xoá sẽ không thể khôi phục. Bạn có chắc chắn muốn xoá?"),
                      onOk: () => deleteAccount(record.id),
                      centered: true,
                      okText: t("Xác nhận", "Xác nhận"),
                      cancelText: t("Huỷ bỏ", "Huỷ bỏ"),
                      footer: (_, { OkBtn, CancelBtn }) => (
                        <>
                          <CancelBtn />
                          <OkBtn />
                        </>
                      ),
                    });
                  }
                },
              }}
              placement="bottomRight"
            >
              <Button>
                <MoreOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
      fixed: "right",
      width: 15,
    },
  ];

  const defaultCheckedList = dataColumnsMinistries.map(
    (item) => item.key as string
  );

  const [checkedList, setCheckedList] = useState(defaultCheckedList);

  useEffect(() => {
    const pageLink = getCurrentPageFromLink(location.search);
    if (!isEmpty(pageLink)) {
      setPageNumber(Number(pageLink));
    } else {
      const fetchDataOptions = async () => {
        const response = await Promise.all([
          getRoles(),
          getUnits(),
          getOffices(),
        ]);
        const dataRoles = response[0].data.data;
        const dataUnits = response[1].data.data;
        const dataOffices = response[2].data.data;
        setDataOptions({
          ...dataOptions,
          dataOffices: dataOffices,
          dataUnits: dataUnits,
          dataRoles: dataRoles,
        });
      };
      fetchDataOptions();
    }
  }, []);

  const columnChecked = useMemo(() => {
    return dataColumnsMinistries.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key as string),
    }));
  }, [checkedList, permissions]);

  const handleOpenCreate = () => {
    setIsEdit(false);
    setIsOpened(true);
  };

  const handleOpenFilter = () => {
    setShowFilter((showFilter) => !showFilter);
  };

  const handleClose = () => {
    setIsOpened(false);
    form.resetFields();
  };

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setParams({ [name]: value });
    setSearchValue(value);
    if (value.length === 0) {
      setSearchValue("");
      setParams();
    }
  };

  const handleSearch = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter") {
      getListData();
    }
  };

  const handleSubmitSearch = () => {
    getListData();
  };

  const clearFilter = () => {
    form.resetFields();
    setFilterData({
      universityId: null,
      status: null,
      approvedDate: null,
    });
  };

  const handleChangeFilter = (value: any, name: string) => {
    setFilterData({ ...filterData, [name]: value });
  };

  const handleCloseFilter = () => {
    setShowFilter(false);
  };

  const onFinishCreate = (values: any) => {
    createAccountMinistry(values)
      .then((res) => {
        const { data, statusCode } = res.data;
        if (statusCode === 200) {
          helpService.successMessage(
            get(data, "message", t("Tạo tài khoản thành công", "Tạo tài khoản thành công"))
          );
          form.resetFields();
          setPageNumber(1);
          handleClose();
        }
      })
      .catch((err) => {});
  };

  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const data =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : "";
    setStatusOrder(data);
    setFieldSortOrder(sorter.field);
  };

  const deleteAccount = async (id: number) => {
    try {
      const res = await deleteAccountMinistry(id);
      if (res.status === 200) {
        message.success(t("Xóa tài khoản thành công", "Xóa tài khoản thành công"));
        if (countItem === 1 && pageNumber > 1) {
          setPageNumber(pageNumber - 1);
        } else {
          getListData();
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Layout className="page-header-group">
      <Form.Provider
        onFormFinish={(name, { values, forms }) => {
          if (name === "createMinistry") {
            const { createMinistry } = forms;
            const data = createMinistry.getFieldsValue(validateFormCreate);
            onFinishCreate(data);
          }
          if (name === "filterForm") {
            getListData();
          }
        }}
      >
        <div
          className={`page-header-group--layout ${
            showFilter ? "open-filter" : ""
          }`}
        >
          <div className="table-header--wrapper">
            <div className="table-header--left">
              <PageHeader title={title} />
            </div>
            <FilterTable
              handleOpen={handleOpenCreate}
              searchValue={searchValue}
              dataColumns={dataColumnsMinistries}
              handleOpenFilter={handleOpenFilter}
              handleSubmitSearch={handleSubmitSearch}
              handleSearch={handleSearch}
              checkedList={checkedList}
              setCheckedList={setCheckedList}
              handleChangeInput={handleChangeInput}
            />
          </div>
          <FormFilter
            handleCloseFilter={handleCloseFilter}
            clearFilter={clearFilter}
            form={form}
            showFilter={showFilter}
            filterData={filterData}
            setFilterData={handleChangeFilter}
          />
        </div>
        <TableData
          dataTable={accountUsers}
          dataColumns={columnChecked}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          pageNumber={pageNumber}
          pageSize={pageSize}
          pagination={true}
          loadingTable={loading}
          scroll={{ x: 1500, y: 600 }}
          totalRecords={totalRecords}
          handleTableChangeProps={handleTableChange}
        />

        <FormCreateMinistry
          open={isOpened}
          handleClose={handleClose}
          fetchData={getListData}
          detailData={detailData}
          isEdit={isEdit}
        />
        <DetailDepartmentalUserAccountDrawer
          open={isOpenLecturerAccountAdmin}
          onClose={() => setIsOpenLecturerAccountAdmin(false)}
          detailData={detailData}
          isEdit={isEdit}
        />

        <ChangePassDepartmentDrawer
          open={isOpenChangePass}
          onClose={() => setIsOpenChangePass(false)}
          detailData={detailData}
          fetchData={getListData}
        />
      </Form.Provider>
    </Layout>
  );
};

export default ListMinistries;
